'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */
/* eslint-disable tailwindcss/no-custom-classname */
import classNames from 'classnames';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { hasOrAwaitsValue } from '../../calculatedAttributes/resolve';
import type { LoanTerms } from '../../calculatedAttributes/store';
import { useCalculatedAttributeResolver } from '../../calculatedAttributes/useCalculatedAttributeResolver';
import {
  ApprovalOddsBadge,
  ApprovalOddsLabels,
} from '../../components/ApprovalOddsBadge';
import { FeLink } from '../../components/FeLink';
import { InlineTooltip } from '../../components/InlineTooltip';
import { MaterialIcon } from '../../components/MaterialIcon';
import { QuoteBasisDisclaimer } from '../../components/QuoteBasisDisclaimer';
import { CmsProduct } from '../../types/cmsProduct';
import { VerticalType } from '../../types/cmsTypes';
import {
  ComponentTypeEnum,
  sendProductAnalytics,
  UserActionEnum,
} from '../../utils/analytics/sendAnalytics';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { getHotRewardOverrideValue } from '../../utils/helpers/attrHelpers';
import { calculateRewardValue } from '../../utils/helpers/calculateRewardValue';
import {
  currencyFormat,
  formatCurrencyAmount,
  formatNumberWithSeparator,
} from '../../utils/helpers/formatters';
import {
  getLoanRewardBreakdown,
  productLoanRewardOffers,
  qualifyingLoanOffers,
} from '../../utils/helpers/personalLoanReward';
import { resolveStackedEffectiveRate } from '../../utils/helpers/stackedEffectiveRate';
import { getHighersRewardExclusiveOffersAndWelcomeOffersGitsFromProduct } from '../../utils/rewards';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { useBreakpoint } from '../../utils/useBreakpoint';
import { createGoToLink } from '../ComparisonTable/ComparisonTable';
import { getCardTitle } from '../ComparisonTable/ComparisonTableHelpers';
import { NA } from '../ComparisonTable/components/Na';
import { TableRuleButton } from '../ComparisonTable/components/tableRuleButton';
import { SingSaverRewardsSectionId } from '../ComparisonTable/constants';
import { getHotRewardDisplayText } from '../ComparisonTable/utils';
import { LoanRateCell } from './components/LoanRateCell';
import { LoanRewardCell } from './components/LoanRewardCell';
import { PriceCell } from './components/PriceCell';
import { TRANSLATIONS } from './i18n';
import { QuickAnswerAttribute, QuickAnswerProps } from './types';
import {
  resolveAttributeAdditionalInfo,
  resolveAttributeCellValue,
} from './utils';

// Responsive product-name column widths (per Figma, CMS-1584).
// Default 156 / 168 / 184 / 206. CC + Hot Reward Pick uses the same widths.
const PRODUCT_COL_WIDTHS = 'w-[156px] md:w-[168px] lg:w-[184px] xl:w-[206px]';

// DVR-resolved values arrive from cms-service wrapped in this exact marker
// (see DvrTokenResolver.wrapResolved). We parse it out and render the value in a
// styled React span (bold + brand accent, CMS-1877 AC11) — item_text is otherwise
// plain text and is NEVER injected via dangerouslySetInnerHTML.
// dotAll: the value can span lines (long-text attribute sub-fields keep authored
// newlines verbatim). Safe with a non-greedy group only because the emitter
// escapes "<" — a literal "</span>" can never occur inside the value.
const DVR_VALUE_RE =
  /<span class="cms-dvr-value" data-dvr-value="true">(.*?)<\/span>/gs;

// The emitter escapes "&", "<", ">" inside the wrapped value so the marker
// stays well-formed even for authored text like "(<5% APR)". Decode the same
// three entities back before rendering — &amp; last, so "&amp;lt;" round-trips
// to the literal "&lt;" the author typed rather than collapsing to "<".
const decodeDvrEntities = (value: string): string =>
  value.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');

const renderSummaryText = (text: string): ReactNode => {
  if (!text.includes('cms-dvr-value')) return text;

  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  DVR_VALUE_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = DVR_VALUE_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    nodes.push(
      <span key={key++} className="font-bold text-text/accent">
        {decodeDvrEntities(match[1])}
      </span>,
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
};

// Local smooth-scroll (duplicated on purpose: cms-components cannot import from
// fe-services, where the sibling Summary block keeps the same 3-liner).
const scrollToAnchor = (anchorId: string) => {
  const target = document.getElementById(anchorId);
  if (target) target.scrollIntoView({ behavior: 'smooth' });
};
/**
 * Price column width (CMS-1956 R8). Fixed at every breakpoint and never
 * expanding: the content is system-generated with a known worst case (a 7-digit
 * premium with separators plus a struck-through original), so the column takes
 * no share of the leftover width and the attribute columns keep it. `w-` plus
 * `min-w-`/`max-w-` because a `<table>` treats a plain width as a suggestion
 * and will still stretch the column to fit its content.
 */
const PRICE_COL_WIDTHS =
  'w-[133px] min-w-[133px] max-w-[133px] md:w-[163px] md:min-w-[163px] md:max-w-[163px]';

type RenderBaseColumnCellOptions = {
  column: NonNullable<QuickAnswerProps['baseColumnOrders']>[number];
  product: CmsProduct;
  rowIndex: number;
  vertical: VerticalType;
  lang: string;
  onRewardCellClick: QuickAnswerProps['onRewardCellClick'];
  naText: string;
  /**
   * Set in Personal Loan mode only: rewards then resolve at the module's loan
   * (CMS-1928 R4), and without one they are "-".
   */
  personalLoan?: { terms: LoanTerms | undefined };
  rewardTooltipOpenOn: 'click' | 'hover';
  approvalOdds: {
    data?: QuickAnswerProps['approvalOddsData'];
    labels: ApprovalOddsLabels;
    onUnlockClick?: () => void;
    onClick?: () => void;
  };
};

const renderBaseColumnCell = ({
  column,
  product,
  rowIndex,
  vertical,
  lang,
  onRewardCellClick,
  naText,
  personalLoan,
  rewardTooltipOpenOn,
  approvalOdds,
}: RenderBaseColumnCellOptions) => {
  const badgeClass = classNames(
    'inline-flex items-center justify-center rounded-radius/tooltip',
    'px-spacing/padding/soft-8px py-spacing/padding/soft-4px',
    'font-font-weight/table-cell-strong text-font-size/table-cell leading-line-height/table-cell',
    'text-fill/primary-dark bg-fill/primary-light',
  );

  switch (column.keyColumn) {
    case 'REWARD_VALUE': {
      if (personalLoan) {
        const breakdown = getLoanRewardBreakdown(
          productLoanRewardOffers(product),
          personalLoan.terms,
        );
        return breakdown ? (
          <LoanRewardCell breakdown={breakdown} openOn={rewardTooltipOpenOn} />
        ) : (
          <NA text={naText} isBlackColor />
        );
      }

      const giftOffers =
        getHighersRewardExclusiveOffersAndWelcomeOffersGitsFromProduct(
          product,
          undefined,
          vertical !== 'PERSONAL_LOANS',
        );
      if (!giftOffers || (!giftOffers.prefix && !giftOffers.price)) {
        return <NA text={naText} isBlackColor />;
      }

      const value =
        vertical === 'PERSONAL_LOANS'
          ? `${currencyFormat(giftOffers.prefix)}${giftOffers.price?.toLocaleString(lang)}`
          : `${currencyFormat(giftOffers.prefix)}${formatNumberWithSeparator({
              value: calculateRewardValue({
                exclusiveOffers: product.exclusive_offers ?? [],
                welcomeOffers: product.welcome_offers ?? [],
              }),
              thousandsSeparator: ',',
            })}`;

      return (
        <button
          type="button"
          onClick={(e) => onRewardCellClick(e, product, rowIndex)}
          className={badgeClass}
        >
          {value}
        </button>
      );
    }
    case 'HOT_REWARD': {
      // With a loan, the usual pick among the gifts that qualify at it (CMS-1928
      // R4). Only Comparison Table injects the Hot Reward Pick, so none is lost;
      // the HOT_REWARD attribute is the editor's own text (CMS-1969) and wins at
      // any loan, as in every other mode.
      const loanTerms = personalLoan?.terms;
      const giftTitle = !personalLoan
        ? getHotRewardDisplayText(product)
        : loanTerms
          ? getHotRewardDisplayText({
              ...product,
              exclusive_offers: qualifyingLoanOffers(
                product.exclusive_offers ?? [],
                loanTerms,
              ),
              bundle_exclusive_offers: qualifyingLoanOffers(
                product.bundle_exclusive_offers ?? [],
                loanTerms,
              ),
            })
          : getHotRewardOverrideValue(product);
      if (!giftTitle || !product.url_alias) {
        return <NA text={naText} isBlackColor />;
      }

      return (
        <FeLink
          href={createGoToLink(
            product.url_alias,
            SingSaverRewardsSectionId,
            vertical,
          )}
          onClick={(e) => onRewardCellClick(e, product, rowIndex)}
          className={badgeClass}
        >
          {giftTitle}
        </FeLink>
      );
    }
    case 'APPROVAL_ODDS': {
      const { data, labels, onUnlockClick, onClick } = approvalOdds;
      // Locked until the user has fresh/expired odds (matches ComparisonTable).
      const isLocked = !data || !['expired', 'fresh'].includes(data.userState);
      const isFresh = data?.userState === 'fresh';
      const degree = product.title ? data?.odds[product.title] : undefined;

      return (
        <ApprovalOddsBadge
          degree={degree}
          isLocked={isLocked}
          isFresh={isFresh}
          labels={labels}
          onUnlockClick={onUnlockClick}
          onClick={onClick}
        />
      );
    }
    default:
      // Any base column we don't specially handle still shows a cell.
      return <NA text={naText} isBlackColor />;
  }
};

export const QuickAnswer = (props: QuickAnswerProps) => {
  const {
    products,
    attributes,
    anchorId,
    anchorLabel,
    baseColumnOrders = [],
    nudgeTitle,
    nudgeDescription,
    exploreLinks,
    displayMode,
    summaryPoints = [],
    vertical,
    approvalOddsData,
    onApprovalOddsUnlockClick,
    onApprovalOddsClick,
    pushDataLayer,
    onProductClick,
    onRewardCellClick,
    slug,
    mode = 'GENERAL',
    defaultInsuranceType,
    defaultTravelAsFamily,
    defaultDestination,
    defaultRegion,
    endDateOffsetDays,
    loanAmount,
    loanTenure,
  } = props;

  // Empty legacy display_mode behaves as comparison (AC2).
  const isSummary = displayMode === 'Summary';

  // A picked anchor can later be deleted or renamed on the page (Text Block /
  // Table Builder). Per the ticket Q&A, such a title falls back to plain bold
  // text instead of rendering a dead link — system-decided, no editor warning.
  // "Gone" includes display:none targets (a desktop-only Table Builder keeps
  // its id in the DOM on mobile but scrollIntoView on it is a no-op), so the
  // check re-runs on resize. checkVisibility is unsupported in jsdom and old
  // Safari — there we fall back to the presence check alone.
  // The DOM check can only run on the client: SSR keeps the link, hydration
  // downgrades it if the target is unusable (so SSR and first client render
  // match).
  const summaryAnchorKey = summaryPoints
    .map((point) => point.itemAnchorId?.trim() ?? '')
    .join('|');
  const [missingAnchors, setMissingAnchors] = useState<string[]>([]);

  useEffect(() => {
    if (!isSummary) return;

    const recheck = () => {
      const missing = summaryAnchorKey.split('|').filter((anchor) => {
        if (!anchor) return false;
        const target = document.getElementById(anchor);
        if (!target) return true;
        return target.checkVisibility ? !target.checkVisibility() : false;
      });

      setMissingAnchors((prev) =>
        prev.length === missing.length && prev.every((a, i) => a === missing[i])
          ? prev
          : missing,
      );
    };

    recheck();
    window.addEventListener('resize', recheck);
    return () => window.removeEventListener('resize', recheck);
  }, [isSummary, summaryAnchorKey]);

  const context = useCmsClientContext();
  const { lang, brand } = context;
  const staticText = getClientTranslations(TRANSLATIONS, context);

  const isTravelInsurance = mode === 'TRAVEL_INSURANCE';
  const isPersonalLoan = mode === 'PERSONAL_LOAN';
  // Optional in Strapi: an incomplete loan is no loan, and its values are "-".
  const loanTerms =
    isPersonalLoan && loanAmount && loanTenure
      ? { amount: loanAmount, tenure: loanTenure }
      : undefined;

  // The engine facade: assembles CalculationSources from context, so this block
  // never builds them by hand and cannot forget a provider's slice. The poll's
  // progress rides along in its answer, so this module reads no quote status.
  const resolveCalculated = useCalculatedAttributeResolver();

  // The disclaimer states the trip behind the PRICES, so it appears only where
  // a price does (CMS-1956 R7). Attribute columns are static Strapi content and
  // carry no date assumption of their own.
  const isPriceAttribute = (attribute: QuickAnswerAttribute) =>
    resolveCalculated(attribute.attribute?.name, undefined)?.provider ===
    'TI_QUOTE';

  const priceAttribute = isTravelInsurance
    ? props.attributes.find(isPriceAttribute)
    : undefined;

  // Whether THIS module priced any of its own rows. The sentence states the
  // basis for the PRICES SHOWN, so it must not sit above an all-"-" column:
  // a quote that settles with no usable premium (`success` with none matching,
  // or `fail`/`timeout`) leaves every cell a dash, and the trip would then be
  // described for prices that are not there. Same rule as Table Builder's
  // `hasResolvedQuotes`.
  const hasResolvedQuotes = useMemo(() => {
    const priceAttributeName = priceAttribute?.attribute?.name;
    if (!priceAttributeName) return false;

    // A price the module has, or one it is still waiting for: both keep the
    // paragraph mounted. Mounted for the whole poll matters because it sits
    // above the table, so unmounting it mid-poll would shift the table down
    // (CLS) — and the cells show loaders meanwhile, which is what it describes.
    return products.some((product) =>
      hasOrAwaitsValue(resolveCalculated(priceAttributeName, product)),
    );
  }, [priceAttribute, products, resolveCalculated]);

  /**
   * The price column is dropped outside Travel Insurance, rather than rendered
   * empty (CMS-1956 R1 / AC-11). The attribute stays selected in Strapi across a
   * mode switch, deliberately, so that switching back restores the column — but
   * in General or Personal Loan there is nothing to quote, and a column of
   * dashes would read as missing data instead of a mode that shows no price.
   * The engine reports such an attribute unavailable here; same split as Table
   * Builder's `isRenderedColumn`.
   */
  const renderedAttributes = attributes.filter(
    (attribute) =>
      resolveCalculated(attribute.attribute?.name, undefined)?.state !==
      'unavailable',
  );

  /**
   * The attribute carrying the table's one emphasised value (CMS-1584). Skips
   * Price, which is never emphasised (R9): placed first it used to take index 0
   * and leave the table with nothing emphasised at all (decision 11.09). Reward
   * columns are `baseColumnOrders`, so they were never candidates here.
   */
  const emphasisIndex = renderedAttributes.findIndex(
    (attribute) => !isPriceAttribute(attribute),
  );
  const productHeader = getCardTitle(vertical ?? 'CREDIT_CARD', staticText);

  const approvalOddsLabels: ApprovalOddsLabels = {
    high: staticText.approval_odds_high,
    medium: staticText.approval_odds_medium,
    low: staticText.approval_odds_low,
    unlock: staticText.approval_odds_unlock,
  };

  // Attribute-info tooltip open mode mirrors the RP comparison table
  // (ComparisonTableBuilder): hover on desktop (>=1280px), tap/click below.
  const helpLogic = useBreakpoint<'click' | 'hover'>({
    _: 'click',
    xxl: 'hover',
  });

  // Horizontal-scroll affordance — mirrors ComparisonTable / TableBuilder.
  const tableRef = useRef<HTMLDivElement | null>(null);
  const [[showLeft, showRight], setShow] = useState([false, false]);

  useEffect(() => {
    const el = tableRef.current;
    if (!el) return;

    const updateOverflow = () => {
      const hasOverflow = el.scrollWidth > el.clientWidth;
      const offsetThrottle = 10;
      setShow([
        !!el.scrollLeft,
        hasOverflow &&
          el.clientWidth + el.scrollLeft <= el.scrollWidth - offsetThrottle,
      ]);
    };

    updateOverflow();

    const ro = new ResizeObserver(updateOverflow);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const tableScrollHandler = useCallback(
    (e: { currentTarget: HTMLDivElement }) => {
      const t = e.currentTarget;
      const offsetThrottle = 10;
      setShow([
        !!t.scrollLeft,
        t.clientWidth + t.scrollLeft <= t.scrollWidth - offsetThrottle,
      ]);
    },
    [],
  );

  // Anchor visibility rules (see CMS-1584 plan):
  //  anchor_id blank          -> no anchor
  //  anchor_id set, label set -> use label
  //  anchor_id set, no label  -> brand default
  const trimmedAnchorId = anchorId?.trim() || undefined;
  const trimmedAnchorLabel = anchorLabel?.trim() || undefined;
  const renderedAnchorLabel = trimmedAnchorId
    ? (trimmedAnchorLabel ?? staticText.default_anchor_label)
    : undefined;

  const trimmedNudgeTitle = nudgeTitle?.trim() || undefined;
  const trimmedNudgeDescription = nudgeDescription?.trim() || undefined;
  const hasStructuredNudge = !!(trimmedNudgeTitle || trimmedNudgeDescription);

  // Drop empty-label related links so we don't render bare separators and so
  // GA `index` stays contiguous (#2210).
  const visibleExploreLinks = exploreLinks?.filter((link) =>
    link.linkLabel?.trim(),
  );

  // The rightmost column keeps the card's inset from its right edge (CMS-1928 R6).
  const lastColumnPadding = (isLast: boolean) =>
    isPersonalLoan && isLast && 'pr-spacing/padding/x';
  const isLastBaseColumn = (index: number) =>
    !renderedAttributes.length && index === baseColumnOrders.length - 1;
  const isLastAttribute = (index: number) =>
    index === renderedAttributes.length - 1;

  const renderCell = (
    attr: QuickAnswerAttribute,
    attrIndex: number,
    product: CmsProduct,
    rowIndex: number,
  ) => {
    // The price attribute is never authored in Strapi: its value comes from the
    // engine, so it must not fall through to the catalogue lookup below, which
    // would find nothing and render the NA placeholder.
    //
    // Whether it is calculated in this module is the engine's answer, by the
    // module's mode (CMS-2052).
    const resolved = resolveCalculated(attr.attribute?.name, product);
    if (resolved?.provider === 'TI_QUOTE') {
      return <PriceCell result={resolved} />;
    }

    // A loan attribute the engine calculates here is its answer at the module's
    // loan, never the catalogue's — so no figure gives "-" (CMS-1928 R2).
    const loanResult =
      resolved?.provider === 'PL_CALCULATOR' ? resolved : undefined;
    const value = loanResult
      ? loanResult.state === 'resolved'
        ? loanResult.value.formatted
        : undefined
      : resolveAttributeCellValue(product, attr);
    if (!value) {
      return <NA text={staticText.na} isBlackColor />;
    }

    const effectiveRate = loanResult
      ? resolveStackedEffectiveRate(
          attr.attribute?.name,
          product,
          brand,
          resolveCalculated,
        )
      : undefined;
    if (effectiveRate) {
      return <LoanRateCell rate={value} effectiveRate={effectiveRate} />;
    }

    const isPrimary = attrIndex === emphasisIndex;
    const valueClassNames = classNames(
      'truncate',
      isPrimary
        ? 'font-font-weight/table-cell-primary text-font-size/table-cell-primary leading-line-height/table-cell-primary text-text/accent'
        : 'font-font-weight/table-cell-normal text-font-size/table-cell leading-line-height/table-cell text-text/primary',
    );

    const additionalInfo = resolveAttributeAdditionalInfo(product, attr);
    if (!additionalInfo) {
      return (
        <span title={value} className={classNames('block', valueClassNames)}>
          {value}
        </span>
      );
    }

    // CMS-1790 — "i" tooltip with the attribute's "Additional information".
    // GA mirrors the RP comparison table tooltip (product_interaction/tooltip).
    const sendTooltipAnalytics = (action: UserActionEnum) => {
      if (!pushDataLayer) return;
      const attributeName = attr.label.trim();
      sendProductAnalytics({
        action,
        componentTitle: attributeName
          ? `Quick Answer/Tooltip_${attributeName}`
          : 'Quick Answer/Tooltip',
        componentType: ComponentTypeEnum.TOOLTIP,
        event: 'product_interaction',
        index: rowIndex,
        product,
        pushDataLayer,
        url: slug,
      });
    };

    // The icon sits right after the (possibly truncated) value, following the
    // text — never pinned to the cell's right edge — and survives truncation.
    // min-w-0 lets the flex item shrink so `truncate` can engage.
    return (
      <span className="flex items-center">
        <span title={value} className={classNames('min-w-0', valueClassNames)}>
          {value}
        </span>
        <InlineTooltip
          placement="top"
          onOpenEvent={helpLogic}
          content={additionalInfo}
          infoClassName="shrink-0 pl-1 text-secondary-lighter"
          onClick={() => sendTooltipAnalytics(UserActionEnum.CLICK)}
          onHover={() => sendTooltipAnalytics(UserActionEnum.HOVER)}
        />
      </span>
    );
  };

  return (
    <div className="flex flex-col gap-spacing/gap/y">
      {/* Title sits OUTSIDE the card, above it. */}
      <h3 className="mhg-alpha-typography-display-1 text-text/primary">
        {staticText.title}
      </h3>

      {/* Summary mode (CMS-1877): two-column static rows, no horizontal scroll
          at any breakpoint. Left = title (tinted), right = text. The title
          column is 120px on mobile only; from 768px up it follows the
          2-col-width token, which already carries a value per breakpoint. */}
      {isSummary && (
        // A definition list keeps the title→description pairing available to
        // assistive tech and snippet extraction (this module targets organic
        // blog traffic). min-w-0 on both cells lets break-words actually wrap
        // unbreakable runs (URLs, promo codes) instead of the card's
        // overflow-hidden clipping them (AC7).
        <dl className="overflow-hidden rounded-radius/table border border-borders/default">
          {summaryPoints.map((point, i) => {
            const isLast = i === summaryPoints.length - 1;
            const pickedAnchor = point.itemAnchorId?.trim() || undefined;
            // Broken/removed anchor → rendered without a link, never a dead one.
            const anchor =
              pickedAnchor && !missingAnchors.includes(pickedAnchor)
                ? pickedAnchor
                : undefined;
            // Every title takes the link colour, anchored or not; the anchor
            // only adds the underline and the click behaviour.
            const titleClass = classNames(
              'min-w-0 break-words',
              'font-font-weight/table-cell-strong text-font-size/table-cell leading-line-height/table-cell',
              'text-text/link',
            );
            return (
              <div
                key={point.id}
                className={classNames('flex', {
                  'border-b border-borders/default': !isLast,
                })}
              >
                <dt className="flex w-30 min-w-0 shrink-0 items-center bg-fill/primary-light px-spacing/padding/x-sm py-spacing/padding/y-sm md:w-general/2-col-width">
                  {anchor ? (
                    <a
                      href={`#${anchor}`}
                      onClick={(e) => {
                        // Prevent the native hash-jump so scrollIntoView's smooth
                        // behaviour actually runs (AC3).
                        e.preventDefault();
                        // component_type is 'table', not the 'text-link' the two
                        // sibling link events use: AirTable #2453 is the spec.
                        pushDataLayer?.({
                          event: 'navigate',
                          component_type: 'table',
                          component_title: `Summary/Quick Answer_${point.itemTitle.trim()}`,
                          action: 'click',
                          index: i.toString(),
                        });
                        scrollToAnchor(anchor);
                      }}
                      className={classNames(titleClass, 'underline')}
                    >
                      {point.itemTitle}
                    </a>
                  ) : (
                    <span className={titleClass}>{point.itemTitle}</span>
                  )}
                </dt>
                <dd className="flex min-w-0 flex-1 items-center bg-fill/card px-spacing/padding/x-sm py-spacing/padding/y-sm">
                  <span
                    className={classNames(
                      'min-w-0 break-words',
                      'font-font-weight/table-cell-normal text-font-size/table-cell leading-line-height/table-cell',
                      'text-text/primary',
                    )}
                  >
                    {renderSummaryText(point.itemText)}
                  </span>
                </dd>
              </div>
            );
          })}
        </dl>
      )}

      {/* Comparison mode: card + horizontal scroll affordance (mirrors ComparisonTable).
          `isolate` confines the sticky first-column z-index (and scroll arrows)
          to this subtree's own stacking context, so they can't paint over the
          AsideModal overlay when a product/reward modal is open. */}
      {!isSummary && (
        <div className="isolate flex flex-col gap-spacing/gap/y-sm">
          {loanTerms && (
            // The description slot the prototype gives it, like the TI
            // disclaimer below; rendered only with both inputs (R7).
            <p className="font-font-weight/table-cell-normal text-font-size/table-cell leading-line-height/table-cell text-text/primary">
              {staticText.loan_basis
                .replace(
                  '{amount}',
                  formatCurrencyAmount(
                    loanTerms.amount,
                    context.currencyDisplay,
                  ),
                )
                .replace('{tenure}', String(loanTerms.tenure))}
            </p>
          )}
          {hasResolvedQuotes && (
            // Inside the card wrap, above the table: the slot the prototype puts
            // it in, one gap tighter than the title's. The wrap spaces its
            // children, so the sentence carries no margin of its own.
            <QuoteBasisDisclaimer
              className="text-text/primary"
              insuranceType={defaultInsuranceType}
              travelAsFamily={defaultTravelAsFamily}
              destination={defaultDestination?.label}
              region={defaultRegion?.label}
              endDateOffsetDays={endDateOffsetDays}
            />
          )}
          {/* The arrows are positioned against the TABLE, not against the card
              wrap: `TableRuleButton` offsets from its positioned ancestor's top
              assuming the 72px header row starts there, so a disclaimer sharing
              that ancestor would push them off the table's centre. */}
          <div className="relative">
            <div
              className={classNames(
                'overflow-hidden rounded-radius/table border border-borders/default',
              )}
            >
              <div
                ref={tableRef}
                className="overflow-x-auto"
                onScroll={tableScrollHandler}
              >
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-fill/alternative-1">
                      <th
                        className={classNames(
                          'sticky left-0 z-10 bg-fill/alternative-1',
                          'min-w-30 px-spacing/padding/x py-spacing/padding/soft-12px text-left align-middle',
                          PRODUCT_COL_WIDTHS,
                        )}
                      >
                        <span
                          className={classNames(
                            'font-font-weight/table-header text-font-size/table-header leading-line-height/table-header',
                            'text-text/secondary',
                          )}
                        >
                          {productHeader}
                        </span>
                      </th>
                      {baseColumnOrders.map((baseColumn, columnIndex) => {
                        let title = baseColumn.nameColumn;

                        if (!title) {
                          switch (baseColumn.keyColumn) {
                            case 'REWARD_VALUE':
                              title = isPersonalLoan
                                ? staticText.total_reward
                                : vertical === 'INVESTMENTS' ||
                                    vertical === 'BANKING'
                                  ? staticText.brand_reward_stk_bka
                                  : staticText.brand_reward;
                              break;
                            case 'HOT_REWARD':
                              title =
                                vertical === 'PERSONAL_LOANS'
                                  ? staticText.trending_reward_pick
                                  : staticText.hot_reward_pick;
                              break;
                            case 'APPROVAL_ODDS':
                              title = staticText.approval_odds;
                              break;
                          }
                        }

                        return (
                          <th
                            key={
                              baseColumn.id ??
                              `${baseColumn.keyColumn}-${columnIndex}`
                            }
                            className={classNames(
                              'bg-fill/alternative-1',
                              'min-w-30 px-spacing/padding/x py-spacing/padding/soft-12px text-left align-middle',
                              PRODUCT_COL_WIDTHS,
                              lastColumnPadding(isLastBaseColumn(columnIndex)),
                            )}
                          >
                            <span
                              className={classNames(
                                'font-font-weight/table-header text-font-size/table-header leading-line-height/table-header',
                                'text-text/secondary whitespace-break-spaces',
                              )}
                            >
                              {title}
                            </span>
                          </th>
                        );
                      })}
                      {renderedAttributes.map((attr, attrIndex) => (
                        <th
                          key={attr.id}
                          className={classNames(
                            'p-spacing/padding/soft-12px text-left align-middle',
                            isTravelInsurance && isPriceAttribute(attr)
                              ? PRICE_COL_WIDTHS
                              : 'min-w-30',
                            lastColumnPadding(isLastAttribute(attrIndex)),
                          )}
                        >
                          <span
                            className={classNames(
                              'font-font-weight/table-header text-font-size/table-header leading-line-height/table-header',
                              'text-text/secondary whitespace-break-spaces',
                            )}
                          >
                            {attr.label}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product, rowIndex) => {
                      const isClickable = !!product.url_alias;
                      const productName =
                        product.product_name ?? product.title ?? '';
                      return (
                        <tr
                          key={product.title ?? product.entryUID ?? rowIndex}
                          className="bg-fill/card border-b border-borders/default"
                        >
                          <td
                            className={classNames(
                              'sticky left-0 z-1 bg-fill/card align-middle',
                              'min-h-table/cell-min-height px-spacing/padding/x py-spacing/padding/y-sm',
                              PRODUCT_COL_WIDTHS,
                            )}
                          >
                            {isClickable ? (
                              <button
                                type="button"
                                onClick={() =>
                                  onProductClick(product, rowIndex)
                                }
                                className={classNames(
                                  'text-left',
                                  'font-font-weight/table-cell-strong text-font-size/table-cell leading-line-height/table-cell',
                                  'text-text/link underline',
                                )}
                              >
                                {productName}
                              </button>
                            ) : (
                              <span
                                className={classNames(
                                  'font-font-weight/table-cell-strong text-font-size/table-cell leading-line-height/table-cell',
                                  'text-text/primary',
                                )}
                              >
                                {productName}
                              </span>
                            )}
                          </td>
                          {baseColumnOrders.map((baseColumn, columnIndex) => (
                            <td
                              key={
                                baseColumn.id ??
                                `${baseColumn.keyColumn}-${columnIndex}`
                              }
                              className={classNames(
                                'min-h-table/cell-min-height p-spacing/padding/soft-12px',
                                PRODUCT_COL_WIDTHS,
                                lastColumnPadding(
                                  isLastBaseColumn(columnIndex),
                                ),
                              )}
                            >
                              {renderBaseColumnCell({
                                column: baseColumn,
                                product,
                                rowIndex,
                                vertical: vertical ?? 'CREDIT_CARD',
                                lang,
                                onRewardCellClick,
                                naText: staticText.na,
                                personalLoan: isPersonalLoan
                                  ? { terms: loanTerms }
                                  : undefined,
                                rewardTooltipOpenOn: helpLogic,
                                approvalOdds: {
                                  data: approvalOddsData,
                                  labels: approvalOddsLabels,
                                  onUnlockClick: onApprovalOddsUnlockClick,
                                  onClick: onApprovalOddsClick,
                                },
                              })}
                            </td>
                          ))}
                          {renderedAttributes.map((attr, attrIndex) => (
                            <td
                              key={attr.id ?? attr.attribute?.name}
                              className={classNames(
                                'min-h-table/cell-min-height p-spacing/padding/soft-12px align-middle',
                                isTravelInsurance &&
                                  isPriceAttribute(attr) &&
                                  PRICE_COL_WIDTHS,
                                lastColumnPadding(isLastAttribute(attrIndex)),
                              )}
                            >
                              {renderCell(attr, attrIndex, product, rowIndex)}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Insight / nudge panel — tinted, with primary accent on top. */}
              <div
                className={classNames(
                  'flex w-full flex-col items-start justify-center',
                  'gap-spacing/gap/y-sm',
                  'px-spacing/margin/x py-spacing/margin/y',
                  'border-t-2 border-fill/primary bg-fill/primary-light',
                  {
                    hidden:
                      !hasStructuredNudge &&
                      !(renderedAnchorLabel && trimmedAnchorId),
                  },
                )}
              >
                {hasStructuredNudge && (
                  <>
                    {trimmedNudgeTitle && (
                      <span
                        className={classNames(
                          'font-font-weight/bullet-2 text-font-size/bullet-2 leading-line-height/bullet-2',
                          'text-fill/primary-dark',
                        )}
                      >
                        {trimmedNudgeTitle}
                      </span>
                    )}
                    {trimmedNudgeDescription && (
                      <span
                        className={classNames(
                          'font-font-weight/table-cell-normal text-font-size/table-cell leading-line-height/table-cell',
                          'text-text/secondary-lighter',
                        )}
                      >
                        {trimmedNudgeDescription}
                      </span>
                    )}
                  </>
                )}

                {renderedAnchorLabel && trimmedAnchorId && (
                  <a
                    href={`#${trimmedAnchorId}`}
                    onClick={() => {
                      pushDataLayer?.({
                        event: 'navigate',
                        component_type: 'text-link',
                        component_title: `${nudgeTitle ?? 'Footer'}/Quick Answer_${renderedAnchorLabel}`,
                        action: 'click',
                        index: '0',
                      });
                    }}
                    className={classNames(
                      'self-start',
                      'font-font-weight/bullet-2 text-font-size/bullet-2 leading-line-height/bullet-2',
                      'text-text/link underline',
                    )}
                  >
                    {renderedAnchorLabel}
                  </a>
                )}
              </div>
            </div>
            <TableRuleButton
              type="left"
              onClick={() => {
                tableRef.current?.scrollTo({
                  left: 0,
                  top: 0,
                  behavior: 'smooth',
                });
              }}
              show={showLeft}
            />
            <TableRuleButton
              type="right"
              onClick={() => {
                tableRef.current?.scrollTo({
                  left: 10000,
                  top: 0,
                  behavior: 'smooth',
                });
              }}
              show={showRight}
            />
          </div>
        </div>
      )}

      {/* Related ("More:") links outside the card. */}
      {visibleExploreLinks && visibleExploreLinks.length > 0 && (
        <div className="flex flex-wrap items-center gap-spacing/gap/soft-6px">
          {/* "≫" restored per Figma (CMS-1877 AC10) — both display modes. */}
          <MaterialIcon
            icon="keyboard_double_arrow_right"
            px={20}
            className="text-text/secondary"
          />
          <span
            className={classNames(
              'font-font-weight/bullet-2 text-font-size/bullet-2 leading-line-height/bullet-2',
              'text-text/secondary',
            )}
          >
            {staticText.more_label}
          </span>
          {visibleExploreLinks.map((link, i) => (
            <span key={link.id} className="inline-flex items-center">
              <FeLink
                href={link.linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  // GA #2210 — Quick Answer related (explore) link click.
                  pushDataLayer?.({
                    event: 'navigate',
                    component_type: 'text-link',
                    component_title: `Related Links/Quick Answer_${link.linkLabel!.trim()}`,
                    action: 'click',
                    index: i.toString(),
                  });
                }}
                className={classNames(
                  'font-font-weight/button-1 text-font-size/button-2 leading-line-height/button-2',
                  'text-text/link underline',
                )}
              >
                {link.linkLabel}
              </FeLink>
              {i < visibleExploreLinks.length - 1 && (
                <span
                  className={classNames(
                    'font-font-weight/button-1 text-font-size/button-2 leading-line-height/button-2',
                    'text-text/primary',
                  )}
                >
                  ,&nbsp;
                </span>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
